import { z } from 'zod'

import { CustomerIdSchema, CustomerSchema } from '../customer/customer.dto'
import { TicketIdSchema } from '../ticket/ticket.dto'
import { UserIdSchema, UserSchema } from '../user/user.dto'

import {
  TicketMessageAttachmentNestedUpsertSchema,
  TicketMessageAttachmentSchema,
} from './ticketMessageAttachment.dto'
import { TicketMessageIdSchema } from './ticketMessageId'
export type { TicketMessageId } from './ticketMessageId'

export const MergeCommentIdSchema = z.string().brand('MergeCommentId')
export type MergeCommentId = z.infer<typeof MergeCommentIdSchema>

export const RemoteTicketMessageIdSchema = z
  .string()
  .brand('RemoteTicketMessageId')
export type RemoteTicketMessageId = z.infer<typeof RemoteTicketMessageIdSchema>

// eslint-disable-next-line no-restricted-syntax -- Don't make more of these enums please!!
export const TicketMessageTypeSchema = z.enum([
  // A message sent by a customer.
  'CUSTOMER',
  // A message sent by a user, i.e. a human agent.
  'USER',
  // An internal note sent by a user, i.e. a human agent.
  'INTERNAL_USER_NOTE',
  // A message sent by our bot.
  'BOT_RESPONSE',
  // A message sent by our bot, where more bot messages are expected.
  'PENDING_RESPONSE',
  // A draft message generated by our bot.
  'DRAFT_RESPONSE',
  // An internal note sent by our bot.
  'INTERNAL_MODEL_NOTE',
  // An internal note sent by our bot but only visible to us.
  'INTERNAL_MODEL_PRIVATE_NOTE',
  // An internal message used to track information requested by our bot to invoke a tool.
  'INTERNAL_TOOL_INVOCATION_REQUEST',
  // An internal message used to track information sent to our bot after invoking a tool.
  'INTERNAL_TOOL_INVOCATION_DATA',
  // A message we couldn't classify.
  'UNKNOWN',
])

export type TicketMessageType = z.infer<typeof TicketMessageTypeSchema>

/**
 * __Ticket Message__
 *
 * A TicketMessage is a single interaction either sent by a user or by a customer.
 */
export const TicketMessageSchema = z.object({
  // The unique ID of this single message.
  id: TicketMessageIdSchema,
  // If this message was originally sourced from a third-party service, this is
  // the ID of the message in that service.
  remoteId: RemoteTicketMessageIdSchema.nullish().transform((val) =>
    val === null ? undefined : val,
  ),
  // If this message was originally sourced from a third-party service, this is
  // the Merge.dev ID of the message.
  mergeId: MergeCommentIdSchema.nullish().transform((val) =>
    val === null ? undefined : val,
  ),
  // The ticket that this message belongs to.
  ticketId: TicketIdSchema,
  // If the message was retrieved from a third-party system via a Merge
  // integration, this is the merge ID of the user to whom the message's
  // ticket has been assigned, if any.
  mergeAssigneeId: z
    .string()
    .nullish()
    .transform((val) => (val === null ? undefined : val)),
  // If this message was sent by a customer, then this is their ID.
  authorCustomerId: CustomerIdSchema.nullish(),
  // If this message was sent by a customer, this is the key customer info.
  customer: CustomerSchema.nullish().transform((val) =>
    val === null ? undefined : val,
  ),
  // If this message was sent by a user, then this is their ID.
  authorUserId: UserIdSchema.nullish(),
  // If this message was sent by a user, this is the key user info.
  user: UserSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
    mergeUserId: true,
    remoteId: true,
  })
    .nullish()
    .transform((val) => (val === null ? undefined : val)),
  /**
   * The plain-text content of the message. Should not contain HTML or other markup, because this is what we currently send through to LLMs.
   * LLMs can handle html okay but having plain text will help them focus a bit, and reduce the chance that they'll try to mark up their own reply when we don't want them to.
   */
  content: z.string(),
  /**
   * The content of the message as it was originally received from a ticketing system,
   * so that we can present this to agents in the same format as the original message.
   *
   * Particularly helpful for ticket feedback and diagnosis.
   * Ideally (to save on space and to have 1x source of truth), we should not have
   * content separate to rawContent; instead have the latter and transform to plain-text when needed.
   * However, adding this to start with them being split to reduce the blast radius of this change
   * then we can gradually move to using rawContent.
   */
  rawContent: z
    .string()
    .nullish()
    .transform((val) => (val === null ? undefined : val)),
  // If the ticket has an attachment (e.g. the user uploaded an image).
  // As of this writing (2024-7-16), attachments are not ingested.
  hasAttachment: z.boolean().optional(),
  // The timestamp at which the message was created.
  createdAt: z.date(),
  // The timestamp at which the message was updated.
  updatedAt: z.date(),
  // The type of message.
  messageType: TicketMessageTypeSchema,
  /**
   * The time the message was created in the remote system
   */
  remoteCreatedAt: z.date().nullish(),
  attachments: z
    .array(TicketMessageAttachmentSchema)
    .nullish()
    .transform((val) => (val === null ? undefined : val)),
})

export type TicketMessage = z.infer<typeof TicketMessageSchema>

// TicketMessageNestedUpsert schema
export const TicketMessageNestedUpsertSchema = TicketMessageSchema.omit({
  id: true,
  ticketId: true,
  createdAt: true,
  updatedAt: true,
  customer: true,
  user: true,
  attachments: true,
}).extend({
  id: TicketMessageIdSchema.optional(),
  ticketId: TicketIdSchema.optional(),
  attachments: z.array(TicketMessageAttachmentNestedUpsertSchema).optional(),
})

export type TicketMessageNestedUpsert = z.infer<
  typeof TicketMessageNestedUpsertSchema
>

// TicketMessageUpsert schema
export const TicketMessageUpsertSchema = TicketMessageSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  attachments: true,
  customer: true,
  user: true,
}).extend({
  createdAt: z.date().optional(),
  attachments: z.array(TicketMessageAttachmentNestedUpsertSchema).optional(),
})

export type TicketMessageUpsert = z.infer<typeof TicketMessageUpsertSchema>

export const TicketMessageWithoutAttachmentsUpsertSchema =
  TicketMessageSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    attachments: true,
    customer: true,
    user: true,
  }).extend({
    ticketId: TicketIdSchema.optional(),
  })

export type TicketMessageWithoutAttachmentsUpsert = z.infer<
  typeof TicketMessageWithoutAttachmentsUpsertSchema
>
